/**
 * Clients, their files, and what their canonical rows look like.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import * as clientsApi from '../api/Clients';
import { parsed } from '../api/parse';
import { MODULE_CODES, type ModuleCode } from '../features/modules/modules';
import {
	canonicalRowPreviewSchema,
	clientRecordSchema,
	fileRecordSchema,
} from '../schemas';
import { keys } from './keys';

const clientsListQuery = (module: ModuleCode) => ({
	queryKey: keys.clients.list(module),
	queryFn: async () =>
		parsed(
			z.array(clientRecordSchema),
			await clientsApi.getClients(module),
			`/clients?module=${module}`,
		),
});

/** Every client of the firm, with this module's file and audit counts. */
export function useClientsQuery(module: ModuleCode) {
	return useQuery(clientsListQuery(module));
}

/**
 * Every module's client list (each holds every client, with that module's
 * counts). For the Clients list (merged with `mergeClientLists`, counts summed)
 * and a client's module page (its counts per module).
 */
export function useEveryModuleClientsQuery(enabled = true) {
	return useQueries({
		queries: MODULE_CODES.map((module) => ({
			...clientsListQuery(module),
			enabled,
		})),
		combine: (results) => ({
			lists: results.map((result) => result.data),
			isPending: results.some((result) => result.isPending),
			error: results.find((result) => result.error)?.error ?? null,
			refetch: () => {
				for (const result of results)
					if (result.error) void result.refetch();
			},
		}),
	});
}

export function useClientFilesQuery(
	clientId: string | null,
	module: ModuleCode,
	enabled = true,
) {
	return useQuery({
		queryKey: keys.clients.files(clientId ?? '', module),
		queryFn: async () =>
			parsed(
				z.array(fileRecordSchema),
				await clientsApi.getClientFiles(clientId as string, module),
				`/files?clientId=${clientId}&module=${module}`,
			),
		enabled: Boolean(clientId) && enabled,
		/* Re-read while a file's rows are being prepared or processed, so its
       status moves on the list without a reload; stops once none is. */
		refetchInterval: (query) =>
			(query.state.data ?? []).some(
				(file) =>
					file.status === 'MAPPING_CONFIRMED' ||
					file.status === 'PROCESSING',
			)
				? 4000
				: false,
	});
}

/**
 * How many canonical rows a filter would take, for this client.
 *
 * The filter string is part of the query key, which is the one real gain over
 * the hook this replaces: that refetched on every keystroke and had no memory,
 * so narrowing a filter and widening it again asked the server twice for an
 * answer it had already given. Here the previous filter is still cached.
 */
export function useCanonicalRowPreviewQuery(
	clientId: string | null,
	module: ModuleCode,
	filterQuery: string,
	enabled = true,
) {
	return useQuery({
		queryKey: keys.clients.preview(clientId ?? '', module, filterQuery),
		queryFn: async () => {
			const path = `/clients/${clientId}/canonical-rows/preview${filterQuery ? `?${filterQuery}` : ''}`;
			return parsed(
				canonicalRowPreviewSchema,
				await clientsApi.getCanonicalRowPreview(
					clientId as string,
					module,
					filterQuery,
				),
				path,
			);
		},
		enabled: Boolean(clientId) && enabled,
	});
}
